import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { useOrgContext } from '@/hooks/useOrgContext';
import { AssignmentsList } from '@/components/insurance/AssignmentsList';
import { PlanSummaryTab } from '@/components/insurance/benefit-plan-tabs/PlanSummaryTab';
import { BenefitsAndLimitsTab } from '@/components/insurance/benefit-plan-tabs/BenefitsAndLimitsTab';
import { PreAuthRequirementsTab } from '@/components/insurance/benefit-plan-tabs/PreAuthRequirementsTab';
import { GeneralExclusionsTab } from '@/components/insurance/benefit-plan-tabs/GeneralExclusionsTab';
import { BenefitSpecificExclTab } from '@/components/insurance/benefit-plan-tabs/BenefitSpecificExclTab';
import { ClaimsAndRulesTab } from '@/components/insurance/benefit-plan-tabs/ClaimsAndRulesTab';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Edit, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TBenefitPlanStatus } from '@/@types/claims';

const STATUS_BADGE: Record<TBenefitPlanStatus, string> = {
  draft: 'bg-yellow-500 hover:bg-yellow-600 text-white',
  active: 'bg-green-600 hover:bg-green-700 text-white',
  archived: 'bg-muted text-muted-foreground',
};

const STATUS_TRANSITIONS: Record<TBenefitPlanStatus, TBenefitPlanStatus[]> = {
  draft: ['active'],
  active: ['archived'],
  archived: ['draft'],
};

export function BenefitPlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrg } = useOrgContext();
  const activeTab = searchParams.get('tab') || 'plan-summary';

  // AS_JUSTIFIED: one-time Id cast from route params
  const planId = id as Id<'benefitPlans'>;

  const plan = useQuery(api.insurance.benefitPlans.get, planId ? { planId } : 'skip');
  const updateStatus = useMutation(api.insurance.benefitPlans.updateStatus);

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  async function handleStatusChange(next: string) {
    if (!plan) return;
    setIsUpdatingStatus(true);
    try {
      await updateStatus({ planId: plan._id, status: next as TBenefitPlanStatus }); // AS_JUSTIFIED: value comes from STATUS_TRANSITIONS typed keys
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  if (plan === undefined) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (plan === null) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Benefit plan not found.</p>
        <Button variant="ghost" className="mt-3" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>
    );
  }

  // AS_JUSTIFIED: Convex return type resolves plan.status as string; we know it is TBenefitPlanStatus from the validator
  const planStatus = plan.status as TBenefitPlanStatus;
  const nextStatuses = STATUS_TRANSITIONS[planStatus];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/insurance/benefit-plans')}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
          </Button>
          <h1 className="text-xl font-semibold text-foreground">{plan.name}</h1>
          <Badge className={cn('text-xs font-medium capitalize', STATUS_BADGE[planStatus])}>
            {plan.status}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value=""
            onValueChange={handleStatusChange}
            disabled={isUpdatingStatus || nextStatuses.length === 0}
          >
            <SelectTrigger className="w-40 h-8 text-sm">
              <SelectValue placeholder="Change status…" />
            </SelectTrigger>
            <SelectContent>
              {nextStatuses.map(s => (
                <SelectItem key={s} value={s} className="capitalize">
                  Move to {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/insurance/benefit-plans/${id}/preview?tab=${activeTab}`)}
          >
            <Eye className="mr-1.5 h-3.5 w-3.5" /> Preview
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/insurance/benefit-plans/new?edit=${plan._id}`)}
          >
            <Edit className="mr-1.5 h-3.5 w-3.5" /> Edit
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={v => setSearchParams({ tab: v }, { replace: true })}
        className="mt-4"
      >
        <div className="flex border-b px-8">
          {[
            { value: 'plan-summary', label: 'Plan Summary' },
            { value: 'benefits-limits', label: 'Benefits & Limits' },
            { value: 'pre-auth', label: 'Pre-Auth' },
            { value: 'general-exclusions', label: 'General Exclusions' },
            { value: 'benefit-exclusions', label: 'Benefit Exclusions' },
            { value: 'claims-rules', label: 'Claims & Rules' },
          ].map(tab => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setSearchParams({ tab: tab.value }, { replace: true })}
              className={cn(
                'px-5 py-3 text-[13px] border-b-2 transition-colors',
                activeTab === tab.value
                  ? 'border-lime-500 text-foreground font-semibold'
                  : 'border-transparent text-muted-foreground font-normal hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <TabsContent value="plan-summary">
          <PlanSummaryTab plan={plan} />
        </TabsContent>
        <TabsContent value="benefits-limits">
          <BenefitsAndLimitsTab plan={plan} />
        </TabsContent>
        <TabsContent value="pre-auth">
          <PreAuthRequirementsTab plan={plan} />
        </TabsContent>
        <TabsContent value="general-exclusions">
          <GeneralExclusionsTab plan={plan} />
        </TabsContent>
        <TabsContent value="benefit-exclusions">
          <BenefitSpecificExclTab plan={plan} />
        </TabsContent>
        <TabsContent value="claims-rules">
          <ClaimsAndRulesTab plan={plan} />
        </TabsContent>
      </Tabs>

      {/* Policy Assignments */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Policy Assignments</CardTitle>
        </CardHeader>
        <CardContent>
          {currentOrg ? (
            <AssignmentsList planId={plan._id} orgId={currentOrg._id} />
          ) : (
            <p className="text-sm text-muted-foreground">No organisation context.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
